/**
 * Cycle Role Context
 *
 * Resolves the current user's active job(s) within a Cycle and its children.
 * This is the single source of truth for which surface to show a user.
 *
 * Surface routing source of truth (after cycle permission overlay removal):
 *
 *   isAdmin || isCycleOwner  → admin surface (primary RBAC: canSync('process.edit'))
 *   live Catchball state     → ball_holder / decision_authority
 *   session.facilitatorId    → facilitator
 *   session.participantIds   → session_participant / expert_value_provider / config_contributor
 *   (default fallback)       → observer (strategic read-only)
 *
 * cycle_participants rows are kept as a participation roster and display label,
 * but the `role` column no longer gates which UI surface a user sees.
 * The former per-cycle role overlay (steward/sponsor/item_proposer → surface
 * mapping) has been removed; all access decisions run through primary RBAC.
 */

import { canSync } from './permissionResolver';
import type { DegapCycle } from '../types/prioritization/degapCycle';
import type { PrioritizationSession } from '../types/prioritization/session';
import type { OrgAwareCatchballResult } from '../hooks/prioritization/useOrgAwareCatchball';

// ── Role taxonomy ─────────────────────────────────────────────────────────────

/**
 * The job contexts a person can have within a Cycle and its children.
 * These are not RBAC roles — they are runtime states resolved from
 * RBAC, Catchball loop state, and session participation.
 *
 * sponsor / observer / item_proposer are kept as display-label types
 * for the participant roster UI but no longer drive surface selection.
 */
export type CycleJobRole =
  | 'cycle_owner'          // Owns / configures the cycle — full admin surface
  | 'decision_authority'   // Sent the ball down; waiting for proposals to return
  | 'ball_holder'          // Holds an active Level Turn; runs sessions, forms proposals
  | 'facilitator'          // Running a live session (open/close, capture input, finalize)
  | 'session_participant'  // Invited to rank items in a force-ranking session
  | 'expert_value_provider'// Invited to provide attribute values (S4/S5 sessions)
  | 'config_contributor'   // Invited to contribute to a configuration session (S1-S3)
  | 'sponsor'              // Display label only — strategic stakeholder
  | 'observer'             // Default fallback — read-only visibility
  | 'item_proposer';       // Display label only — pool is open for suggestions

/** The eight distinct UI surfaces. Multiple roles may share a surface. */
export type CycleSurfaceId =
  | 'admin'                // Full CycleDetail + SessionDetail — admin only
  | 'decision_authority'   // Decision Authority command view
  | 'ball_holder'          // Ball Holder surface (Catchball)
  | 'facilitator'          // Session Run tab with facilitator controls elevated
  | 'participant'          // Focused ranking/value entry surface
  | 'config_contributor'   // Focused single-question configuration surface
  | 'strategic'            // Read-only progress view (default for non-workflow users)
  | 'item_proposer';       // Item suggestion form

/** Priority order for primary surface selection (higher index = higher priority). */
const ROLE_PRIORITY: CycleJobRole[] = [
  'observer',
  'sponsor',
  'item_proposer',
  'config_contributor',
  'expert_value_provider',
  'session_participant',
  'facilitator',
  'decision_authority',
  'ball_holder',
  'cycle_owner',
];

const ROLE_TO_SURFACE: Record<CycleJobRole, CycleSurfaceId> = {
  cycle_owner:           'admin',
  decision_authority:    'decision_authority',
  ball_holder:           'ball_holder',
  facilitator:           'facilitator',
  session_participant:   'participant',
  expert_value_provider: 'participant',
  config_contributor:    'config_contributor',
  sponsor:               'strategic',
  observer:              'strategic',
  item_proposer:         'strategic',
};

// ── Context output ────────────────────────────────────────────────────────────

export interface UserCycleRoleContext {
  /** All roles the user currently holds in this cycle context. */
  roles: CycleJobRole[];
  /**
   * The highest-priority active role.
   * Use this to determine the default surface to render.
   */
  primaryRole: CycleJobRole;
  /**
   * The surface that corresponds to the primary role.
   * This is what CycleMyWorkPanel renders.
   */
  activeSurface: CycleSurfaceId;
  /**
   * True if the user has CYCLE_CONFIGURE capability (workspace admin or equivalent).
   * When true, always show the full admin surface regardless of primaryRole.
   */
  isAdmin: boolean;
  /**
   * True if the user is the owner of this cycle (cycle.ownerId === userId).
   */
  isCycleOwner: boolean;
}

// ── Resolver ──────────────────────────────────────────────────────────────────

export interface ResolveCycleRolesInput {
  userId: string;
  workspaceId: string | null | undefined;
  cycle: DegapCycle;
  /** All sessions for this cycle. */
  sessions: PrioritizationSession[];
  /** Result from useOrgAwareCatchball for the active loop, if any. */
  orgAware?: OrgAwareCatchballResult | null;
}

/**
 * Pure resolver — no React, no hooks.
 * Compose with useUserCycleRoleContext for reactive use.
 */
export function resolveUserCycleRoles(input: ResolveCycleRolesInput): UserCycleRoleContext {
  const { userId, workspaceId, cycle, sessions, orgAware } = input;

  if (!userId) {
    return makeContext(['observer'], false, false);
  }

  // ── Admin bypass ──────────────────────────────────────────────────────────
  // 'process.edit' maps to the CYCLE_CONFIGURE capability in capabilityIds.ts
  const isAdmin = workspaceId
    ? canSync('process.edit', { kind: 'resource', id: workspaceId })
    : false;

  // isCycleOwner: simple ownership fact from degap_cycles columns.
  // Not a stored cycle_participants role — just who created or owns the cycle.
  const isCycleOwner = cycle.ownerId === userId || cycle.createdBy === userId;

  const roles = new Set<CycleJobRole>();

  // Workspace admin and cycle owner both get the admin surface
  if (isCycleOwner || isAdmin) {
    roles.add('cycle_owner');
  }

  // ── Ball / Loop roles (live Catchball state) ───────────────────────────────
  if (orgAware) {
    if (orgAware.ballIsWithMe && orgAware.canActOnLoop) {
      roles.add('ball_holder');
    } else if (orgAware.userTier === 'executive' && !orgAware.ballIsWithMe) {
      // Executive who sent the ball down — it's out waiting for return
      roles.add('decision_authority');
    }
  }

  // ── Session roles (live session state) ────────────────────────────────────
  for (const session of sessions) {
    const isParticipant = session.participantIds?.includes(userId) ?? false;
    const isFacilitator = session.facilitatorId === userId;

    if (isFacilitator && ['CRITERIA_FINALIZED', 'RUN_COMPLETE'].includes(session.state)) {
      roles.add('facilitator');
    }

    if (isParticipant) {
      // Map SessionIntent → job role
      // rank_priorities = S6 force-ranking: session_participant
      // converge_values = S5 value entry: expert_value_provider
      // select_attributes / select_item_pool = config contributor
      const intent = session.intent;
      if (intent === 'rank_priorities') {
        roles.add('session_participant');
      } else if (intent === 'converge_values') {
        roles.add('expert_value_provider');
      } else if (
        intent === 'select_attributes' ||
        intent === 'select_item_pool' ||
        intent === 'calibration' ||
        intent === 'review'
      ) {
        roles.add('config_contributor');
      }
    }
  }

  // ── Default: anyone with workspace access who has no active workflow job ───
  // All workspace members can view cycles read-only. No cycle_participants row
  // is required for read access — workspace membership is the gate (Layer 0).
  if (roles.size === 0) {
    roles.add('observer');
  }

  return makeContext([...roles], isAdmin, isCycleOwner);
}

function makeContext(
  roles: CycleJobRole[],
  isAdmin: boolean,
  isCycleOwner: boolean,
): UserCycleRoleContext {
  const primaryRole = [...roles].sort(
    (a, b) => ROLE_PRIORITY.indexOf(b) - ROLE_PRIORITY.indexOf(a),
  )[0] ?? 'observer';

  const activeSurface: CycleSurfaceId =
    isAdmin || isCycleOwner ? 'admin' : ROLE_TO_SURFACE[primaryRole];

  return { roles, primaryRole, activeSurface, isAdmin, isCycleOwner };
}
